/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { EnergyDial } from '../components/EnergyDial.js';
import { PageHeader } from '../components/PageHeader.js';
import { Commitments } from '../components/regions/Commitments.js';
import { Inbox } from '../components/regions/Inbox.js';
import { Shutdown } from '../components/regions/Shutdown.js';
import { localTimezone, monoLabel, today } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * The Letter — the daily driver.
 *
 * Three vertically-stacked regions in a single centered column:
 *
 *   1. Brief   — energy intake pre-Engage; rendered brief post-Engage
 *   2. Inbox   — captures list + brain-dump intake (captures tRPC)
 *   3. Shutdown — close the day
 *
 * Longer-horizon surfaces (the desk, waiting-on, projects, goals, the
 * weekly review) live on their own pages; this page is what the user
 * keeps open through the day to refer back to the brief, drop in new
 * thoughts, and close out.
 */
export function LetterPage() {
  const [physical, setPhysical] = useState(3);
  const [mental, setMental] = useState(3);
  const [emotional, setEmotional] = useState(3);

  const briefDate = today();
  const todayQuery = trpc.briefs.today.useQuery({ briefDate });
  const generate = trpc.briefs.generate.useMutation();

  const onEngage = () => {
    generate.mutate({
      briefDate,
      timezone: localTimezone(),
      energy: { physical, mental, emotional },
      recoveryInputs: {
        sleepHours: null,
        exercise: null,
        phoneManaged: null,
      },
    });
  };

  // Prefer the just-generated brief if the user engaged this session;
  // fall back to whatever is persisted server-side for today.
  const view = generate.data ?? todayQuery.data;
  const evening = isEvening();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader label={t.nav.letter} />

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 620,
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
          }}
        >
          {/* Region 1 — Brief or energy intake */}
          <section style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {todayQuery.isPending ? (
              <span style={monoLabel}>…</span>
            ) : view ? (
              <LetterRendered
                briefMarkdown={view.briefMarkdown}
                posture={view.posture}
                priorities={view.suggestedPriorities}
                observation={view.coachingObservation}
              />
            ) : (
              <EnergyIntake
                physical={physical}
                mental={mental}
                emotional={emotional}
                onPhysical={setPhysical}
                onMental={setMental}
                onEmotional={setEmotional}
                onEngage={onEngage}
                pending={generate.isPending}
                error={generate.error?.message ?? null}
              />
            )}
          </section>

          {/* Region 1.5 — Commitments hang off the brief; only after it lands */}
          {view ? (
            <Commitments briefDate={briefDate} suggestedPriorities={view.suggestedPriorities} />
          ) : null}

          {/* Evening: nudge toward closing the day without hiding anything */}
          {view && evening ? <WindDownNudge /> : null}

          {/* Region 2 — Inbox (captures) */}
          <Inbox />

          {/* Region 3 — Close the day (shutdown). Emphasized in the evening. */}
          <div
            id="shutdown"
            style={
              evening
                ? { borderTop: `2px solid ${p.accent}`, paddingTop: 24 }
                : undefined
            }
          >
            <Shutdown briefDate={briefDate} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * True after 17:00 in the user's local time — the window where the
 * shutdown ritual is worth surfacing.
 */
function isEvening(): boolean {
  return new Date().getHours() >= 17;
}

/**
 * A slim banner that floats the shutdown ritual up in the evening. It
 * scrolls to the (always-present) shutdown section rather than moving it,
 * so pinned commitments and the capture box stay where they are for a
 * user working late.
 */
function WindDownNudge() {
  return (
    <button
      type="button"
      aria-label="Close the day"
      onClick={() => {
        document.getElementById('shutdown')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 16px',
        background: p.bgAlt,
        border: `1px solid ${p.accent}`,
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          color: p.fg,
        }}
      >
        The day's winding down.
      </span>
      <span style={{ ...monoLabel, color: p.accent }}>Close the day →</span>
    </button>
  );
}

interface LetterRenderedProps {
  briefMarkdown: string;
  posture: 'protected' | 'guarded' | 'siege';
  priorities: ReadonlyArray<{ ordinal: number; description: string }>;
  observation: string;
}

function LetterRendered({ briefMarkdown, posture, priorities, observation }: LetterRenderedProps) {
  return (
    <>
      <span style={monoLabel}>{t.letter.sectionLabel}</span>

      <div
        className="serif"
        style={{
          fontSize: 17,
          lineHeight: 1.55,
          color: p.fg,
        }}
      >
        <ReactMarkdown>{briefMarkdown}</ReactMarkdown>
      </div>

      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontSize: 14,
          color: p.muted,
          fontFeatureSettings: 'var(--serif-feat)',
        }}
      >
        {t.letter.signoff}
      </div>

      <div
        style={{
          marginTop: 12,
          borderTop: `1px solid ${p.line}`,
          borderBottom: `1px solid ${p.line}`,
          display: 'grid',
          gridTemplateColumns: '40px 1fr',
          rowGap: 0,
        }}
      >
        {priorities.map((pr, i) => (
          <PriorityRow
            key={pr.ordinal}
            index={pr.ordinal}
            description={pr.description}
            isFirst={i === 0}
          />
        ))}
      </div>

      <div style={{ ...monoLabel, marginTop: 6 }}>Posture · {posture}</div>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 15,
          color: p.muted,
          margin: 0,
        }}
      >
        {observation}
      </p>
    </>
  );
}

function PriorityRow({
  index,
  description,
  isFirst,
}: {
  index: number;
  description: string;
  isFirst: boolean;
}) {
  const borderTop = isFirst ? 'none' : `1px solid ${p.line}`;
  return (
    <>
      <div style={{ ...monoLabel, padding: '14px 0', borderTop }}>{`0${index}`}</div>
      <div style={{ padding: '14px 0', fontSize: 14, borderTop }}>{description}</div>
    </>
  );
}

function EnergyIntake({
  physical,
  mental,
  emotional,
  onPhysical,
  onMental,
  onEmotional,
  onEngage,
  pending,
  error,
}: {
  physical: number;
  mental: number;
  emotional: number;
  onPhysical: (v: number) => void;
  onMental: (v: number) => void;
  onEmotional: (v: number) => void;
  onEngage: () => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <>
      <span style={monoLabel}>{t.letter.energyHeader}</span>
      <p
        style={{
          fontFamily: 'var(--font-serif)',
          fontFeatureSettings: 'var(--serif-feat)',
          fontSize: 16,
          lineHeight: 1.55,
          color: p.muted,
          margin: 0,
        }}
      >
        {t.letter.energyHint}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        <EnergyDial label={t.letter.energyLabels.physical} value={physical} onChange={onPhysical} />
        <EnergyDial label={t.letter.energyLabels.mental} value={mental} onChange={onMental} />
        <EnergyDial
          label={t.letter.energyLabels.emotional}
          value={emotional}
          onChange={onEmotional}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button
          type="button"
          onClick={onEngage}
          disabled={pending}
          style={{
            padding: '10px 18px',
            background: p.fg,
            color: p.bg,
            border: 'none',
            fontSize: 13,
            cursor: pending ? 'wait' : 'pointer',
            opacity: pending ? 0.6 : 1,
          }}
        >
          {pending ? t.letter.generating : `${t.letter.generate} →`}
        </button>
      </div>
      {error ? <p style={{ ...monoLabel, color: p.accent }}>{error}</p> : null}
    </>
  );
}
